import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Chip,
  Tooltip,
  IconButton,
  Autocomplete,
  Paper,
  Alert
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { ROLES } from '../../constants/roles';
import { utcToDisplayDateAndTime, displayDateTimeToUTC, getTimezoneLabel } from '../../utils/timezoneUtils';
import { UNIT_METRIC_OPTIONS, unitMetricLabel } from '../../constants/unitMetrics';

const PartForm = ({
  open,
  onClose,
  selectedPart,
  formData,
  setFormData,
  formTab,
  setFormTab,
  dropdownOptions,
  displayTimezone,
  onSubmit,
  onDelete,
  user,
  onRefreshDropdowns,
  onOpenSerialDependencies
}) => {
  // Track if we've already refreshed dropdowns for this dialog session
  const hasRefreshedRef = React.useRef(false);
  // History tab state
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  // Fetch part history when History tab is selected (edit mode only)
  useEffect(() => {
    if (!open || !selectedPart?._id || formTab !== 4) {
      return;
    }
    let cancelled = false;
    setHistoryError(null);
    setHistoryLoading(true);
    axios.get(`/api/parts/${selectedPart._id}/history`)
      .then((res) => {
        if (!cancelled) setHistoryEvents(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        if (!cancelled) setHistoryError(err.response?.data?.message || 'Failed to load history');
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, selectedPart?._id, formTab]);

  // Reset history state when dialog closes or part changes
  useEffect(() => {
    if (!open) {
      setHistoryEvents([]);
      setHistoryError(null);
    }
  }, [open, selectedPart?._id]);

  // Refresh dropdowns when dialog opens (only once per open session)
  React.useEffect(() => {
    if (open && onRefreshDropdowns && !hasRefreshedRef.current) {
      hasRefreshedRef.current = true;
      onRefreshDropdowns();
    }
    // Reset when dialog closes
    if (!open) {
      hasRefreshedRef.current = false;
    }
  }, [open, onRefreshDropdowns]); // Note: onRefreshDropdowns should be wrapped in useCallback in parent component
  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [field]: value });
  };

  const handleMultiSelectChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  // Filter primary models based on selected manufacturer names
  const filteredPrimaryModels = React.useMemo(() => {
    if (!formData.manufacturerNames || formData.manufacturerNames.length === 0) {
      return [];
    }
    return (dropdownOptions.primaryModels || []).filter(model => {
      // Handle both populated (object) and unpopulated (string ID) manufacturer references
      let manufacturerId;
      if (typeof model.manufacturer === 'object' && model.manufacturer !== null) {
        // Populated: get _id from the object
        manufacturerId = model.manufacturer._id ? String(model.manufacturer._id) : String(model.manufacturer);
      } else {
        // Unpopulated: use the ID directly
        manufacturerId = String(model.manufacturer);
      }
      // Convert manufacturerNames to strings for comparison
      const selectedManufacturerIds = (formData.manufacturerNames || []).map(id => String(id));
      return selectedManufacturerIds.includes(manufacturerId);
    });
  }, [formData.manufacturerNames, dropdownOptions.primaryModels]);

  // Filter model variants based on selected primary models
  const filteredModelVariants = React.useMemo(() => {
    if (!formData.primaryModels || formData.primaryModels.length === 0) {
      return [];
    }
    return (dropdownOptions.modelVariants || []).filter(variant => {
      const primaryModelId = typeof variant.primaryModel === 'object' && variant.primaryModel !== null
        ? variant.primaryModel._id || variant.primaryModel
        : variant.primaryModel;
      return formData.primaryModels.includes(primaryModelId);
    });
  }, [formData.primaryModels, dropdownOptions.modelVariants]);

  const canDelete = [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.CA_MANAGER, ROLES.MRO_MANAGER].includes(user?.role);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {selectedPart ? 'Edit Part' : 'Add Part'}
      </DialogTitle>
      <DialogContent>
        <Tabs value={formTab} onChange={(e, v) => setFormTab(v)} sx={{ mb: 2 }}>
          <Tab label="Basic Information" />
          <Tab label="Details" />
          <Tab label="Inventory" />
          <Tab label="Additional" />
          {selectedPart && <Tab label="History" />}
        </Tabs>

        {formTab === 0 && (
          <Grid container spacing={2}>
            {selectedPart && selectedPart.partId && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Part ID"
                  value={selectedPart.partId}
                  disabled
                  helperText="System generated unique identifier"
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Part Number"
                value={formData.partNumber}
                onChange={handleChange('partNumber')}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reference Part Number"
                value={formData.referencePartNumber || ''}
                onChange={handleChange('referencePartNumber')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required sx={{ minWidth: 200 }}>
                <InputLabel
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'text.primary',
                    backgroundColor: 'background.paper',
                    padding: '0 4px',
                    whiteSpace: 'nowrap',
                    overflow: 'visible',
                    '&.MuiInputLabel-shrink': {
                      fontSize: '14px',
                      fontWeight: 600,
                      transform: 'translate(14px, -9px) scale(0.75)',
                      backgroundColor: 'background.paper',
                      padding: '0 4px',
                      color: 'text.primary',
                    },
                    '&.Mui-focused': {
                      color: 'primary.main',
                      fontWeight: 600,
                    },
                    '&.MuiInputLabel-root': {
                      zIndex: 1,
                    },
                  }}
                >
                  Unit Metric
                </InputLabel>
                <Select
                  value={formData.unitMetric}
                  label="Unit Metric"
                  onChange={handleChange('unitMetric')}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      overflow: 'visible',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 400,
                        minWidth: 200,
                        '& .MuiMenuItem-root': {
                          padding: '10px 16px',
                          minHeight: 'auto',
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {UNIT_METRIC_OPTIONS.map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {unitMetricLabel(unit)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={{ minWidth: 200 }}>
                <InputLabel
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'text.primary',
                    backgroundColor: 'background.paper',
                    padding: '0 4px',
                    whiteSpace: 'nowrap',
                    overflow: 'visible',
                    '&.MuiInputLabel-shrink': {
                      fontSize: '14px',
                      fontWeight: 600,
                      transform: 'translate(14px, -9px) scale(0.75)',
                      backgroundColor: 'background.paper',
                      padding: '0 4px',
                      color: 'text.primary',
                    },
                    '&.Mui-focused': {
                      color: 'primary.main',
                      fontWeight: 600,
                    },
                    '&.MuiInputLabel-root': {
                      zIndex: 1,
                    },
                  }}
                >
                  Frequency
                </InputLabel>
                <Select
                  value={formData.frequency ?? ''}
                  label="Frequency"
                  onChange={handleChange('frequency')}
                  displayEmpty
                  renderValue={(value) => (value === '' ? 'None' : value)}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      overflow: 'visible',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 400,
                        minWidth: 200,
                        '& .MuiMenuItem-root': {
                          padding: '10px 16px',
                          minHeight: 'auto',
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="Cycles">Cycles</MenuItem>
                  <MenuItem value="Landings">Landings</MenuItem>
                  <MenuItem value="Hours">Hours</MenuItem>
                  <MenuItem value="Days">Days</MenuItem>
                  <MenuItem value="Years">Years</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Part Name"
                value={formData.partName ?? ''}
                onChange={handleChange('partName')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleChange('description')}
                required
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required sx={{ minWidth: 200 }}>
                <InputLabel
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'text.primary',
                    backgroundColor: 'background.paper',
                    padding: '0 4px',
                    whiteSpace: 'nowrap',
                    overflow: 'visible',
                    '&.MuiInputLabel-shrink': {
                      fontSize: '14px',
                      fontWeight: 600,
                      transform: 'translate(14px, -9px) scale(0.75)',
                      backgroundColor: 'background.paper',
                      padding: '0 4px',
                      color: 'text.primary',
                    },
                    '&.Mui-focused': {
                      color: 'primary.main',
                      fontWeight: 600,
                    },
                    '&.MuiInputLabel-root': {
                      zIndex: 1,
                    },
                  }}
                >
                  Part Category
                </InputLabel>
                <Tooltip 
                  title={dropdownOptions.categories.find(cat => cat._id === formData.category)?.name || ''}
                  placement="top"
                  arrow
                  disableHoverListener={!formData.category}
                >
                  <Select
                    value={formData.category}
                    label="Part Category"
                    onChange={handleChange('category')}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        overflow: 'visible',
                      },
                    }}
                    renderValue={(value) => {
                      const category = dropdownOptions.categories.find(cat => cat._id === value);
                      return category?.name || '';
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 400,
                          minWidth: 350,
                          '& .MuiMenuItem-root': {
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            padding: '10px 16px',
                            minHeight: 'auto',
                            lineHeight: 1.5,
                            overflow: 'visible',
                          },
                        },
                      },
                    }}
                  >
                    {dropdownOptions.categories.map((cat) => (
                      <MenuItem key={cat._id} value={cat._id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Tooltip>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required sx={{ minWidth: 200 }}>
                <InputLabel
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'text.primary',
                    backgroundColor: 'background.paper',
                    padding: '0 4px',
                    whiteSpace: 'nowrap',
                    overflow: 'visible',
                    '&.MuiInputLabel-shrink': {
                      fontSize: '14px',
                      fontWeight: 600,
                      transform: 'translate(14px, -9px) scale(0.75)',
                      backgroundColor: 'background.paper',
                      padding: '0 4px',
                      color: 'text.primary',
                    },
                    '&.Mui-focused': {
                      color: 'primary.main',
                      fontWeight: 600,
                    },
                    '&.MuiInputLabel-root': {
                      zIndex: 1,
                    },
                  }}
                >
                  Manufacturer Name
                </InputLabel>
                <Select
                  multiple
                  value={formData.manufacturerNames || []}
                  label="Manufacturer Name"
                  onChange={handleMultiSelectChange('manufacturerNames')}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      overflow: 'visible',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 400,
                        minWidth: 350,
                        '& .MuiMenuItem-root': {
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          padding: '10px 16px',
                          minHeight: 'auto',
                          lineHeight: 1.5,
                          overflow: 'visible',
                        },
                      },
                    },
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const manufacturerName = dropdownOptions.manufacturerNames.find(mn => mn._id === value);
                        return <Chip key={value} label={manufacturerName?.name || value} size="small" />;
                      })}
                    </Box>
                  )}
                >
                  {(dropdownOptions.manufacturerNames || []).map((mn) => (
                    <MenuItem key={mn._id} value={mn._id}>
                      <Checkbox checked={(formData.manufacturerNames || []).indexOf(mn._id) > -1} />
                      {mn.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={{ minWidth: 200 }}>
                <InputLabel
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'text.primary',
                    backgroundColor: 'background.paper',
                    padding: '0 4px',
                    whiteSpace: 'nowrap',
                    overflow: 'visible',
                    '&.MuiInputLabel-shrink': {
                      fontSize: '14px',
                      fontWeight: 600,
                      transform: 'translate(14px, -9px) scale(0.75)',
                      backgroundColor: 'background.paper',
                      padding: '0 4px',
                      color: 'text.primary',
                    },
                    '&.Mui-focused': {
                      color: 'primary.main',
                      fontWeight: 600,
                    },
                    '&.MuiInputLabel-root': {
                      zIndex: 1,
                    },
                  }}
                >
                  Primary Model
                </InputLabel>
                <Select
                  multiple
                  value={formData.primaryModels || []}
                  label="Primary Model"
                  onChange={(e) => {
                    setFormData({ 
                      ...formData, 
                      primaryModels: e.target.value,
                      modelVariants: [] // Clear model variants when primary models change
                    });
                  }}
                  disabled={!formData.manufacturerNames || formData.manufacturerNames.length === 0}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      overflow: 'visible',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 400,
                        minWidth: 350,
                        '& .MuiMenuItem-root': {
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          padding: '10px 16px',
                          minHeight: 'auto',
                          lineHeight: 1.5,
                          overflow: 'visible',
                        },
                      },
                    },
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const model = filteredPrimaryModels.find(m => m._id === value);
                        return <Chip key={value} label={model?.name || value} size="small" />;
                      })}
                    </Box>
                  )}
                >
                  {filteredPrimaryModels.length === 0 ? (
                    <MenuItem disabled>
                      {formData.manufacturerNames && formData.manufacturerNames.length > 0
                        ? 'No primary models found for selected manufacturers'
                        : 'Select manufacturer names first'}
                    </MenuItem>
                  ) : (
                    filteredPrimaryModels.map((model) => (
                      <MenuItem key={model._id} value={model._id}>
                        <Checkbox checked={(formData.primaryModels || []).indexOf(model._id) > -1} />
                        {model.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={{ minWidth: 200 }}>
                <InputLabel
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'text.primary',
                    backgroundColor: 'background.paper',
                    padding: '0 4px',
                    whiteSpace: 'nowrap',
                    overflow: 'visible',
                    '&.MuiInputLabel-shrink': {
                      fontSize: '14px',
                      fontWeight: 600,
                      transform: 'translate(14px, -9px) scale(0.75)',
                      backgroundColor: 'background.paper',
                      padding: '0 4px',
                      color: 'text.primary',
                    },
                    '&.Mui-focused': {
                      color: 'primary.main',
                      fontWeight: 600,
                    },
                    '&.MuiInputLabel-root': {
                      zIndex: 1,
                    },
                  }}
                >
                  Model Variant
                </InputLabel>
                <Select
                  multiple
                  value={formData.modelVariants || []}
                  label="Model Variant"
                  onChange={handleMultiSelectChange('modelVariants')}
                  disabled={!formData.primaryModels || formData.primaryModels.length === 0}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      overflow: 'visible',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 400,
                        minWidth: 350,
                        '& .MuiMenuItem-root': {
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          padding: '10px 16px',
                          minHeight: 'auto',
                          lineHeight: 1.5,
                          overflow: 'visible',
                        },
                      },
                    },
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const variant = filteredModelVariants.find(v => v._id === value);
                        return <Chip key={value} label={variant?.name || value} size="small" />;
                      })}
                    </Box>
                  )}
                >
                  {filteredModelVariants.length === 0 ? (
                    <MenuItem disabled>
                      {formData.primaryModels && formData.primaryModels.length > 0
                        ? 'No model variants found for selected primary models'
                        : 'Select primary models first'}
                    </MenuItem>
                  ) : (
                    filteredModelVariants.map((variant) => (
                      <MenuItem key={variant._id} value={variant._id}>
                        <Checkbox checked={(formData.modelVariants || []).indexOf(variant._id) > -1} />
                        {variant.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required sx={{ minWidth: 200 }}>
                <InputLabel
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'text.primary',
                    backgroundColor: 'background.paper',
                    padding: '0 4px',
                    whiteSpace: 'nowrap',
                    overflow: 'visible',
                    '&.MuiInputLabel-shrink': {
                      fontSize: '14px',
                      fontWeight: 600,
                      transform: 'translate(14px, -9px) scale(0.75)',
                      backgroundColor: 'background.paper',
                      padding: '0 4px',
                      color: 'text.primary',
                    },
                    '&.Mui-focused': {
                      color: 'primary.main',
                      fontWeight: 600,
                    },
                    '&.MuiInputLabel-root': {
                      zIndex: 1,
                    },
                  }}
                >
                  Part Type
                </InputLabel>
                <Select
                  value={formData.partType}
                  label="Part Type"
                  onChange={handleChange('partType')}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      overflow: 'visible',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 400,
                        minWidth: 200,
                        '& .MuiMenuItem-root': {
                          padding: '10px 16px',
                          minHeight: 'auto',
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="Vendor Part">Vendor Part</MenuItem>
                  <MenuItem value="OEM Part">OEM Part</MenuItem>
                  <MenuItem value="Alternate Part">Alternate Part</MenuItem>
                  <MenuItem value="Superseed Part">Superseed Part</MenuItem>
                  <MenuItem value="Variant Part">Variant Part</MenuItem>
                  <MenuItem value="Standard Part">Standard Part</MenuItem>
                  <MenuItem value="PMA Part">PMA Part</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                sx={{ minWidth: 95 }}
                options={dropdownOptions.ataChapters || []}
                getOptionLabel={(option) => {
                  if (!option || !option._id) return '';
                  return `${option.code} - ${option.name}`;
                }}
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter((option) => {
                    const codeMatch = option.code?.toLowerCase().includes(searchTerm);
                    const nameMatch = option.name?.toLowerCase().includes(searchTerm);
                    const descMatch = option.description?.toLowerCase().includes(searchTerm);
                    return codeMatch || nameMatch || descMatch;
                  });
                }}
                isOptionEqualToValue={(option, value) => {
                  const optionId = option?._id?.toString() || option?._id;
                  const valueId = value?._id?.toString() || value?._id;
                  return optionId === valueId;
                }}
                value={dropdownOptions.ataChapters.find(ata => {
                  const ataId = ata?._id?.toString() || ata?._id;
                  return ataId === (formData.ataChapter?.toString() || formData.ataChapter);
                }) || null}
                onChange={(event, newValue) => {
                  const ataId = newValue ? (newValue._id?.toString() || newValue._id) : '';
                  handleChange('ataChapter')({ target: { value: ataId } });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="ATA Chapter"
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        overflow: 'visible',
                      },
                    }}
                  />
                )}
                renderOption={(props, ata) => (
                  <Box component="li" {...props} key={ata._id}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {ata.code} - {ata.name}
                      </Typography>
                      {ata.description && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                          {ata.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                ListboxProps={{
                  style: {
                    maxHeight: 400
                  }
                }}
                PaperComponent={({ children, ...other }) => (
                  <Paper
                    {...other}
                    sx={{
                      minWidth: 800,
                      maxWidth: 900,
                      '& .MuiAutocomplete-option': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        padding: '10px 16px',
                        minHeight: 'auto',
                        lineHeight: 1.5,
                        overflow: 'visible',
                      },
                    }}
                  >
                    {children}
                  </Paper>
                )}
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 8],
                        },
                      },
                    ],
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.serialNumberRequired}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      if (checked) {
                        setFormData({
                          ...formData,
                          serialNumberRequired: true,
                          serialNumbers: formData.serialNumbers && formData.serialNumbers.length > 0 ? formData.serialNumbers : ['']
                        });
                        return;
                      }
                      const nonEmpty = (formData.serialNumbers || []).map((s) => (s || '').trim()).filter(Boolean);
                      if (nonEmpty.length === 0) {
                        setFormData({ ...formData, serialNumberRequired: false, serialNumbers: [] });
                        return;
                      }
                      const applyUncheck = () =>
                        setFormData({ ...formData, serialNumberRequired: false, serialNumbers: [] });
                      if (!onOpenSerialDependencies) {
                        applyUncheck();
                        return;
                      }
                      if (!selectedPart?._id) {
                        onOpenSerialDependencies(
                          {
                            serialNumber: nonEmpty[0],
                            partNumber: formData.partNumber || '—',
                            dependencies: {},
                            hasDependencies: false
                          },
                          applyUncheck
                        );
                        return;
                      }
                      Promise.all(
                        nonEmpty.map((sn) =>
                          axios.get(`/api/parts/${selectedPart._id}/serial-number-dependencies`, {
                            params: { serialNumber: sn }
                          })
                        )
                      )
                        .then((results) => {
                          const firstWithDeps = results.find((r) => r.data.hasDependencies);
                          const payload = (firstWithDeps || results[0])?.data;
                          if (payload && onOpenSerialDependencies) {
                            onOpenSerialDependencies(payload, applyUncheck);
                          } else {
                            applyUncheck();
                          }
                        })
                        .catch(() => applyUncheck());
                    }}
                  />
                }
                label="Serial Number Required"
              />
            </Grid>
            {formData.serialNumberRequired && (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Serial Numbers
                </Typography>
                {(formData.serialNumbers || ['']).map((serialNumber, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                    <TextField
                      fullWidth
                      label={`Serial Number ${index + 1}`}
                      value={serialNumber}
                      onChange={(e) => {
                        const newSerialNumbers = [...(formData.serialNumbers || [''])];
                        newSerialNumbers[index] = e.target.value;
                        setFormData({ ...formData, serialNumbers: newSerialNumbers });
                      }}
                      required={formData.serialNumberRequired && index === 0}
                    />
                    {index === (formData.serialNumbers || ['']).length - 1 ? (
                      <IconButton
                        color="primary"
                        onClick={() => {
                          const newSerialNumbers = [...(formData.serialNumbers || ['']), ''];
                          setFormData({ ...formData, serialNumbers: newSerialNumbers });
                        }}
                        sx={{ mt: 1 }}
                        title="Add Serial Number"
                      >
                        <Add />
                      </IconButton>
                    ) : (
                      <IconButton
                        color="error"
                        onClick={() => {
                          const currentSerials = formData.serialNumbers || [''];
                          const value = (currentSerials[index] || '').trim();
                          const doRemove = () => {
                            const newSerialNumbers = [...currentSerials];
                            newSerialNumbers.splice(index, 1);
                            setFormData({
                              ...formData,
                              serialNumbers: newSerialNumbers.length > 0 ? newSerialNumbers : ['']
                            });
                          };
                          if (!selectedPart?._id || !value || !onOpenSerialDependencies) {
                            doRemove();
                            return;
                          }
                          axios
                            .get(`/api/parts/${selectedPart._id}/serial-number-dependencies`, {
                              params: { serialNumber: value }
                            })
                            .then((res) => {
                              if (res.data.hasDependencies && onOpenSerialDependencies) {
                                onOpenSerialDependencies(res.data, doRemove);
                              } else {
                                doRemove();
                              }
                            })
                            .catch(() => doRemove());
                        }}
                        sx={{ mt: 1 }}
                        title="Remove Serial Number"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Grid>
            )}
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                sx={{ minWidth: 95 }}
                multiple
                options={dropdownOptions.assemblyTypes || []}
                getOptionLabel={(option) => option.name || ''}
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter((option) => {
                    return option.name?.toLowerCase().includes(searchTerm);
                  });
                }}
                isOptionEqualToValue={(option, value) => {
                  const optionId = option?._id?.toString() || option?._id;
                  const valueId = value?._id?.toString() || value?._id;
                  return optionId === valueId;
                }}
                value={(formData.applicableAssemblyTypes || []).map(id => {
                  return dropdownOptions.assemblyTypes.find(at => {
                    const atId = at?._id?.toString() || at?._id;
                    return atId === (id?.toString() || id);
                  });
                }).filter(Boolean)}
                onChange={(event, newValue) => {
                  const selectedIds = newValue.map(item => {
                    return item?._id?.toString() || item?._id;
                  });
                  handleMultiSelectChange('applicableAssemblyTypes')({ target: { value: selectedIds } });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Applicable Assembly Types"
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        overflow: 'visible',
                      },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {value.map((option, index) => (
                      <Chip
                        key={option._id}
                        label={option.name}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))}
                  </Box>
                )}
                renderOption={(props, option, { selected }) => (
                  <Box component="li" {...props} key={option._id}>
                    <Checkbox checked={selected} />
                    {option.name}
                  </Box>
                )}
                ListboxProps={{
                  style: {
                    maxHeight: 400
                  }
                }}
                PaperComponent={({ children, ...other }) => (
                  <Paper
                    {...other}
                    sx={{
                      minWidth: 600,
                      maxWidth: 800,
                      '& .MuiAutocomplete-option': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        padding: '10px 16px',
                        minHeight: 'auto',
                        lineHeight: 1.5,
                        overflow: 'visible',
                      },
                    }}
                  >
                    {children}
                  </Paper>
                )}
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 8],
                        },
                      },
                    ],
                  },
                }}
              />
            </Grid>
          </Grid>
        )}

        {formTab === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="IPC Reference"
                value={formData.ipcReference || ''}
                onChange={handleChange('ipcReference')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bin Card Number"
                value={formData.binCardNumber || ''}
                onChange={handleChange('binCardNumber')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Folio Number"
                value={formData.folioNumber || ''}
                onChange={handleChange('folioNumber')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={{ minWidth: 200 }}>
                <InputLabel
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'text.primary',
                    backgroundColor: 'background.paper',
                    padding: '0 4px',
                    whiteSpace: 'nowrap',
                    overflow: 'visible',
                    '&.MuiInputLabel-shrink': {
                      fontSize: '14px',
                      fontWeight: 600,
                      transform: 'translate(14px, -9px) scale(0.75)',
                      backgroundColor: 'background.paper',
                      padding: '0 4px',
                      color: 'text.primary',
                    },
                    '&.Mui-focused': {
                      color: 'primary.main',
                      fontWeight: 600,
                    },
                    '&.MuiInputLabel-root': {
                      zIndex: 1,
                    },
                  }}
                >
                  ABC Type
                </InputLabel>
                <Select
                  value={formData.abcType || ''}
                  label="ABC Type"
                  onChange={handleChange('abcType')}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      overflow: 'visible',
                    },
                  }}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="A">A</MenuItem>
                  <MenuItem value="B">B</MenuItem>
                  <MenuItem value="C">C</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Item Tags (comma-separated)"
                value={formData.itemTags?.join(', ') || ''}
                onChange={(e) => {
                  const tags = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                  setFormData({ ...formData, itemTags: tags });
                }}
                placeholder="e.g., Dangerous Goods, Electrostatic Material"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Approximate Rate"
                type="number"
                value={formData.approximateRate || ''}
                onChange={handleChange('approximateRate')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="AMM/CMM Reference"
                value={formData.ammCmmReference || ''}
                onChange={handleChange('ammCmmReference')}
              />
            </Grid>
          </Grid>
        )}

        {formTab === 2 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Minimum Level"
                type="number"
                value={formData.minimumLevel || ''}
                onChange={handleChange('minimumLevel')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Maximum Level"
                type="number"
                value={formData.maximumLevel || ''}
                onChange={handleChange('maximumLevel')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.reOrderLevelRequired || false}
                    onChange={handleChange('reOrderLevelRequired')}
                  />
                }
                label="Reorder Level Required"
              />
            </Grid>
            {formData.reOrderLevelRequired && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Reorder Level"
                  type="number"
                  value={formData.reOrderLevel || ''}
                  onChange={handleChange('reOrderLevel')}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.expiryItem || false}
                    onChange={handleChange('expiryItem')}
                  />
                }
                label="Expiry Item"
              />
            </Grid>
            {formData.expiryItem && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={{ minWidth: 200 }}>
                    <InputLabel>Expiry type</InputLabel>
                    <Select
                      value={formData.expiryType || 'months'}
                      label="Expiry type"
                      onChange={handleChange('expiryType')}
                    >
                      <MenuItem value="specific_date">Set a specific expiry date</MenuItem>
                      <MenuItem value="months">Months</MenuItem>
                      <MenuItem value="cycles">Cycles</MenuItem>
                      <MenuItem value="landings">Landings</MenuItem>
                      <MenuItem value="hours">Hours</MenuItem>
                      <MenuItem value="days">Days</MenuItem>
                      <MenuItem value="years">Years</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {formData.expiryType === 'specific_date' ? (
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label={displayTimezone ? `Expiry date (${getTimezoneLabel(displayTimezone)})` : 'Expiry date'}
                        value={formData.expirySpecificDate ? (displayTimezone ? dayjs(utcToDisplayDateAndTime(formData.expirySpecificDate, displayTimezone).date) : dayjs(formData.expirySpecificDate)) : null}
                        onChange={(date) => setFormData({
                          ...formData,
                          expirySpecificDate: date ? (displayTimezone ? (displayDateTimeToUTC(date.format('YYYY-MM-DD'), '00:00', displayTimezone) ?? date.toISOString()) : date.toISOString()) : ''
                        })}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                    </LocalizationProvider>
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={`Expiry value (${formData.expiryType || 'months'})`}
                      type="number"
                      inputProps={{ min: 0 }}
                      value={formData.expiryPeriodValue ?? ''}
                      onChange={handleChange('expiryPeriodValue')}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Storage Life (Months)"
                    type="number"
                    value={formData.storageLifeInMonth || ''}
                    onChange={handleChange('storageLifeInMonth')}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.kit || false}
                    onChange={handleChange('kit')}
                  />
                }
                label="Kit"
              />
            </Grid>
          </Grid>
        )}

        {formTab === 3 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.showInValuationReport || false}
                    onChange={handleChange('showInValuationReport')}
                  />
                }
                label="Show in Valuation Report"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.showInStockReport || false}
                    onChange={handleChange('showInStockReport')}
                  />
                }
                label="Show in Stock Report"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.oneTimePurchase || false}
                    onChange={handleChange('oneTimePurchase')}
                  />
                }
                label="One Time Purchase"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.airworthinessCheckRequired || false}
                    onChange={handleChange('airworthinessCheckRequired')}
                  />
                }
                label="Airworthiness Check Required"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Note"
                value={formData.note || ''}
                onChange={handleChange('note')}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        )}

        {formTab === 4 && selectedPart && (
          <Box sx={{ mt: 2 }}>
            {historyLoading && (
              <Typography variant="body2" color="text.secondary">Loading history...</Typography>
            )}
            {historyError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHistoryError(null)}>
                {historyError}
              </Alert>
            )}
            {!historyLoading && !historyError && (
              <Paper elevation={1} sx={{ p: 2.5 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                  Audit timeline
                </Typography>
                {historyEvents.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No history records.</Typography>
                ) : (
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {historyEvents.map((ev) => (
                      <Box component="li" key={ev.id} sx={{ mb: 2, listStyle: 'none' }}>
                        <Typography variant="body2">
                          <strong>{ev.action}</strong>
                          {ev.date && ` — ${new Date(ev.date).toLocaleString()}`}
                          {ev.who && ` by ${(ev.who.firstName || '')} ${(ev.who.lastName || '')}`.trim()}
                        </Typography>
                        {ev.summary && ev.summary.length > 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ pl: 1, mt: 0.25 }}>
                            {ev.summary.join('; ')}
                          </Typography>
                        )}
                        {ev.changes && ev.changes.length > 0 && (
                          <Box sx={{ pl: 1.5, mt: 1, borderLeft: '2px solid', borderColor: 'divider', py: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                              Evidence of change (before → after):
                            </Typography>
                            {ev.changes.map((c, idx) => (
                              <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                                <strong>{c.field}:</strong>{' '}
                                <Box component="span" sx={{ color: 'text.secondary' }}>{c.before}</Box>
                                {' → '}
                                <Box component="span" sx={{ color: 'primary.main', fontWeight: 500 }}>{c.after}</Box>
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {canDelete && selectedPart && onDelete && (
          <Button onClick={onDelete} color="error">
            Archive
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSubmit(false)} variant="contained">
          {selectedPart ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartForm;
